import ReplayIcon from '@mui/icons-material/Replay';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import chartColorPalette from 'assets/styles/chartColorPalette';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  useMasterFilter,
} from 'components/common/MasterFilter';
import PageTitle from 'components/UI/PageTitle';
import moment from 'moment';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import { type AiUsageMetricsResponse, useGetAiUsageMetricsMutation } from 'services/api/aiUsage.api';
import { NAVY, RADIUS_CARD, SHADOW_CARD, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import HardMetricCard from '../../Dashboard/components/HardMetricCard';

const CACHED_COLOR = YELLOW; // the one accent: cached tokens are the insight of this dashboard
const PROMPT_COLOR = alpha(NAVY, 0.55);
const COMPLETION_COLOR = alpha(NAVY, 0.3);

const formatTokens = (value: number | null): string => {
  const tokens = value ?? 0;
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}k`;
  return `${tokens}`;
};

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <Box
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW_CARD,
      p: 2.5,
      height: '100%',
    }}
  >
    <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: '1rem' }}>{title}</Typography>
    {subtitle && <Typography sx={{ color: alpha(NAVY, 0.55), fontSize: '0.8rem', mb: 1 }}>{subtitle}</Typography>}
    {children}
  </Box>
);

const AiUsage: React.FC = () => {
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(undefined);
  const [getAiUsageMetrics, { data, isLoading, isError }] = useGetAiUsageMetricsMutation();
  const [showAverages, setShowAverages] = useState(false);

  const filterConfig = useMemo<FilterFieldConfig[]>(
    () => [
      {
        key: 'dateRange',
        kind: 'dateRange',
        label: 'Fecha',
        placement: 'bar',
        defaultValue: {
          start: moment().subtract(24, 'hours'),
          end: moment().endOf('day'),
        } satisfies DateRangeValue,
      },
      { key: 'businessUnitId', kind: 'singleSelect', label: 'Empresa', placement: 'bar' },
      { key: 'models', kind: 'multiSelect', label: 'Modelo', placement: 'bar' },
      { key: 'providers', kind: 'multiSelect', label: 'Proveedor', placement: 'bar' },
      { key: 'callsites', kind: 'multiSelect', label: 'Origen', placement: 'bar' },
    ],
    []
  );

  const filter = useMasterFilter(filterConfig, null, undefined, undefined, { urlNamespace: 'aiUsage' });
  const dateRange = filter.values.dateRange as DateRangeValue | undefined;
  const selectedBusinessUnitId = filter.values.businessUnitId as string | undefined;
  const selectedModels = (filter.values.models as string[] | undefined) ?? [];
  const selectedProviders = (filter.values.providers as string[] | undefined) ?? [];
  const selectedCallsites = (filter.values.callsites as string[] | undefined) ?? [];

  const dynamicOptions = useMemo(
    () => ({
      businessUnitId: (businessUnits ?? []).map((businessUnit) => ({
        value: String(businessUnit.id),
        label: businessUnit.name,
      })),
      models: (data?.availableModels ?? []).map(({ model, provider }) => ({
        value: model,
        label: model,
        group: provider,
      })),
      providers: (data?.availableProviders ?? []).map((provider) => ({ value: provider, label: provider })),
      callsites: (data?.availableCallsites ?? []).map((callsite) => ({ value: callsite, label: callsite })),
    }),
    [businessUnits, data?.availableModels, data?.availableProviders, data?.availableCallsites]
  );

  const fetchMetrics = useCallback(() => {
    if (!dateRange?.start || !dateRange?.end) return;
    getAiUsageMetrics({
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
      ...(selectedBusinessUnitId ? { businessUnitId: Number(selectedBusinessUnitId) } : {}),
      ...(selectedModels.length ? { models: selectedModels } : {}),
      ...(selectedProviders.length ? { providers: selectedProviders } : {}),
      ...(selectedCallsites.length ? { callsites: selectedCallsites } : {}),
    });
  }, [
    getAiUsageMetrics,
    dateRange?.start,
    dateRange?.end,
    selectedBusinessUnitId,
    selectedModels,
    selectedProviders,
    selectedCallsites,
  ]);

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; "Aplicar" refetches
  }, []);

  const metrics: AiUsageMetricsResponse | undefined = data;
  const series = metrics?.series ?? [];
  const isHourly = metrics?.granularity === 'hour';
  const bucketLabels = series.map((point) => moment(point.bucket).format(isHourly ? 'DD MMM HH:00' : 'DD MMM'));
  const cachedTokens = series.map((point) => point.cachedTokens);
  const uncachedPromptTokens = series.map((point) => Math.max(0, point.promptTokens - point.cachedTokens));
  const completionTokens = series.map((point) => point.completionTokens);
  const costSeries = series.map((point) => point.costUSD);
  const seriesByModel = metrics?.seriesByModel ?? [];
  // Default axis height (25px) clips the tick labels — reserve room and angle the time labels
  const timeXAxis = {
    scaleType: 'point' as const,
    data: bucketLabels,
    height: 60,
    tickLabelStyle: { angle: -45, textAnchor: 'end' as const, fontSize: 11 },
  };

  const topModels = (metrics?.byModel ?? []).slice(0, 8);
  const providers = metrics?.byProvider ?? [];
  const topCallsites = (metrics?.byCallsite ?? []).slice(0, 10);
  const isEmpty = !!metrics && metrics.totals.requests === 0;
  const periodCaption =
    dateRange?.start && dateRange?.end
      ? `${dateRange.start.format('DD MMM YYYY')} — ${dateRange.end.format('DD MMM YYYY')}`
      : '';

  return (
    <Box sx={{ px: 3, pt: 2, pb: 4 }}>
      <PageTitle>Uso de IA</PageTitle>
      <Typography sx={{ color: alpha(NAVY, 0.6), mb: 2, fontSize: '0.9rem' }}>
        Tokens, tasa de aciertos de caché y costo estimado por modelo y proveedor
      </Typography>

      <MasterFilter {...filter} dynamicOptions={dynamicOptions} actionLabel="Aplicar" onAction={fetchMetrics} />

      {isError && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{ color: NAVY, mb: 2 }}>No se pudieron cargar las métricas de uso de IA.</Typography>
          <Button variant="contained" startIcon={<ReplayIcon />} onClick={fetchMetrics}>
            Reintentar
          </Button>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 3 }}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="rounded" height={124} sx={{ borderRadius: RADIUS_CARD }} />
          ))}
          <Skeleton variant="rounded" height={320} sx={{ borderRadius: RADIUS_CARD, gridColumn: '1 / -1' }} />
        </Box>
      )}

      {!isLoading && !isError && isEmpty && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{ color: NAVY }}>
            Sin uso de IA en este período. Cambia el rango de fechas o el filtro de empresa.
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && metrics && !isEmpty && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
              mt: 3,
            }}
          >
            <HardMetricCard
              type="Requests IA"
              data={metrics.totals.requests}
              tooltip="Mensajes con uso de IA registrado en el período"
            />
            <HardMetricCard
              type="Tokens totales"
              data={metrics.totals.totalTokens}
              tooltip="Prompt + completion tokens del período"
            />
            <HardMetricCard
              type="Cache hit rate"
              data={metrics.totals.cacheHitRate}
              percentage
              tooltip="Tokens de prompt leídos desde caché / tokens de prompt totales"
            />
            <HardMetricCard
              type="Costo estimado (USD)"
              data={Number(metrics.totals.costUSD.toFixed(2))}
              tooltip="Costo estimado con precios de lista por modelo (no considera precios de contexto extendido)"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <ChartCard title="Tokens por período" subtitle="Prompt en caché vs sin caché y completion">
              <LineChart
                height={300}
                xAxis={[timeXAxis]}
                yAxis={[{ valueFormatter: (value: number) => formatTokens(value) }]}
                series={[
                  {
                    data: cachedTokens,
                    label: 'Prompt (caché)',
                    color: CACHED_COLOR,
                    area: true,
                    stack: 'tokens',
                    showMark: false,
                  },
                  {
                    data: uncachedPromptTokens,
                    label: 'Prompt (sin caché)',
                    color: PROMPT_COLOR,
                    area: true,
                    stack: 'tokens',
                    showMark: false,
                  },
                  {
                    data: completionTokens,
                    label: 'Completion',
                    color: COMPLETION_COLOR,
                    area: true,
                    stack: 'tokens',
                    showMark: false,
                  },
                ]}
              />
            </ChartCard>
            <ChartCard title="Tokens por modelo" subtitle="Top modelos por tokens del período">
              <LineChart
                height={300}
                xAxis={[timeXAxis]}
                yAxis={[{ valueFormatter: (value: number) => formatTokens(value) }]}
                series={seriesByModel.map((model, index) => ({
                  data: model.tokens,
                  label: model.model,
                  color: chartColorPalette[index % chartColorPalette.length],
                  area: true,
                  stack: 'models',
                  showMark: false,
                }))}
              />
            </ChartCard>
            <ChartCard
              title="Cache hit rate por modelo en el tiempo"
              subtitle="% de tokens de prompt servidos desde caché"
            >
              <LineChart
                height={300}
                xAxis={[timeXAxis]}
                yAxis={[{ min: 0, max: 100, valueFormatter: (value: number) => `${value}%` }]}
                series={seriesByModel.map((model, index) => ({
                  data: model.cacheHitRate.map((rate) => Number((rate * 100).toFixed(1))),
                  label: model.model,
                  color: chartColorPalette[index % chartColorPalette.length],
                  showMark: false,
                }))}
              />
            </ChartCard>
            <ChartCard title="Costo estimado (USD)" subtitle="Por período">
              <LineChart
                height={300}
                xAxis={[timeXAxis]}
                series={[{ data: costSeries, label: 'Por período', color: PROMPT_COLOR, showMark: false }]}
                hideLegend
              />
            </ChartCard>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <ChartCard title="Cache hit rate por proveedor" subtitle="Ordenado por tokens del período">
              <BarChart
                height={Math.max(220, providers.length * 48)}
                layout="horizontal"
                yAxis={[{ scaleType: 'band', data: providers.map((provider) => provider.provider), width: 120 }]}
                xAxis={[{ min: 0, max: 100, height: 35, valueFormatter: (value: number) => `${value}%` }]}
                series={[
                  {
                    data: providers.map((provider) => Number((provider.cacheHitRate * 100).toFixed(1))),
                    color: CACHED_COLOR,
                    valueFormatter: (value: number | null) => `${value ?? 0}%`,
                  },
                ]}
                hideLegend
              />
            </ChartCard>
            <ChartCard title="Cache hit rate por modelo" subtitle="Top modelos por tokens">
              <BarChart
                height={Math.max(220, topModels.length * 48)}
                layout="horizontal"
                yAxis={[
                  {
                    scaleType: 'band',
                    data: topModels.map((model) => model.model),
                    width: 210,
                    tickLabelStyle: { fontSize: 11 },
                  },
                ]}
                xAxis={[{ min: 0, max: 100, height: 35, valueFormatter: (value: number) => `${value}%` }]}
                series={[
                  {
                    data: topModels.map((model) => Number((model.cacheHitRate * 100).toFixed(1))),
                    color: CACHED_COLOR,
                    valueFormatter: (value: number | null) => `${value ?? 0}%`,
                  },
                ]}
                hideLegend
              />
            </ChartCard>
            <ChartCard title="Tokens por origen" subtitle="Desde dónde se pidió cada completion">
              <BarChart
                height={Math.max(220, topCallsites.length * 44)}
                layout="horizontal"
                yAxis={[
                  {
                    scaleType: 'band',
                    data: topCallsites.map((callsite) => callsite.callsite),
                    width: 210,
                    tickLabelStyle: { fontSize: 11 },
                  },
                ]}
                xAxis={[{ height: 35, valueFormatter: (value: number) => formatTokens(value) }]}
                series={[
                  {
                    data: topCallsites.map((callsite) => callsite.totalTokens),
                    color: PROMPT_COLOR,
                    valueFormatter: (value: number | null) => formatTokens(value),
                  },
                ]}
                hideLegend
              />
            </ChartCard>
            <ChartCard title="Cache hit rate por origen" subtitle="Top orígenes por tokens">
              <BarChart
                height={Math.max(220, topCallsites.length * 44)}
                layout="horizontal"
                yAxis={[
                  {
                    scaleType: 'band',
                    data: topCallsites.map((callsite) => callsite.callsite),
                    width: 210,
                    tickLabelStyle: { fontSize: 11 },
                  },
                ]}
                xAxis={[{ min: 0, max: 100, height: 35, valueFormatter: (value: number) => `${value}%` }]}
                series={[
                  {
                    data: topCallsites.map((callsite) => Number((callsite.cacheHitRate * 100).toFixed(1))),
                    color: CACHED_COLOR,
                    valueFormatter: (value: number | null) => `${value ?? 0}%`,
                  },
                ]}
                hideLegend
              />
            </ChartCard>
          </Box>

          <Box sx={{ mt: 2 }}>
            <ChartCard title="Detalle por modelo" subtitle={periodCaption}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FormControlLabel
                  control={
                    <Switch size="small" checked={showAverages} onChange={(e) => setShowAverages(e.target.checked)} />
                  }
                  label={
                    <Typography sx={{ color: alpha(NAVY, 0.7), fontSize: '0.8rem' }}>Promedios por request</Typography>
                  }
                />
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: NAVY, fontWeight: 600 }}>Modelo</TableCell>
                    <TableCell sx={{ color: NAVY, fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Requests
                    </TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Prompt
                    </TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Caché
                    </TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Completion
                    </TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Hit rate
                    </TableCell>
                    <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                      Costo (USD)
                    </TableCell>
                    {showAverages && (
                      <>
                        <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                          Prom. prompt
                        </TableCell>
                        <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                          Prom. completion
                        </TableCell>
                        <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                          Prom. tokens
                        </TableCell>
                        <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                          Prom. costo (USD)
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(metrics.byModel ?? []).map((model) => (
                    <TableRow key={model.model} hover>
                      <TableCell sx={{ color: NAVY }}>{model.model}</TableCell>
                      <TableCell sx={{ color: alpha(NAVY, 0.7) }}>{model.provider}</TableCell>
                      <TableCell align="right" sx={{ color: NAVY }}>
                        {model.requests.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ color: NAVY }}>
                        {formatTokens(model.promptTokens)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: NAVY }}>
                        {formatTokens(model.cachedTokens)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: NAVY }}>
                        {formatTokens(model.completionTokens)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: NAVY, fontWeight: 600 }}>
                        {formatPercent(model.cacheHitRate)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: NAVY }}>
                        {model.costUSD.toFixed(2)}
                      </TableCell>
                      {showAverages && (
                        <>
                          <TableCell align="right" sx={{ color: alpha(NAVY, 0.7) }}>
                            {formatTokens(Math.round(model.promptTokens / model.requests))}
                          </TableCell>
                          <TableCell align="right" sx={{ color: alpha(NAVY, 0.7) }}>
                            {formatTokens(Math.round(model.completionTokens / model.requests))}
                          </TableCell>
                          <TableCell align="right" sx={{ color: alpha(NAVY, 0.7) }}>
                            {formatTokens(Math.round(model.totalTokens / model.requests))}
                          </TableCell>
                          <TableCell align="right" sx={{ color: alpha(NAVY, 0.7) }}>
                            {(model.costUSD / model.requests).toFixed(4)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ChartCard>
          </Box>

          <Typography sx={{ color: alpha(NAVY, 0.45), fontSize: '0.75rem', mt: 2 }}>
            {periodCaption} · granularidad {isHourly ? 'horaria' : 'diaria'} · costo estimado con precios de lista
            {metrics.boundIds ? ` · mensajes #${metrics.boundIds.minId}–#${metrics.boundIds.maxId}` : ''}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AiUsage;
